import { authClient } from './auth-client';
import { pendingWrites } from './query';
import { uploadQueue } from './upload-queue';
import { failedWriteLog } from './failed-writes';
import { identity, type Identity } from './identity';
import type { AccountRef } from './account';

/**
 * Sign out AND destroy the tenant's cached data. The query cache is persisted to IndexedDB
 * for 24h (offline-first), so without this a shared shop computer keeps a readable copy of
 * one user's org data after they sign out.
 *
 * The ONE sign-out: every control that signs out comes through here (MAP 225a), guarded by
 * `useSignOutGuard`. Other tabs are told before the cookie goes and again once it has. `to`
 * is where the page reloads, the front door unless the caller has a reason (an invite page
 * keeps its invite).
 *
 * Signing out KEEPS this person's unsent work on the device for their next sign-in here
 * (MAP 225b). In this order:
 *
 *  1. other tabs are told a change of account is coming;
 *  2. this tab's queued writes stop: the write guard refuses every send from it, and paused
 *     writes leave memory while their rows stay; photos stop draining;
 *  3. the cookie goes; then, whatever happened to that call, the saved cache is removed (no
 *     `queryClient.clear()`: it dropped queued writes, and saved once per query), other tabs
 *     are told, and the page reloads.
 *
 * A write already on the wire when the cookie goes is delivered (its row deleted), kept by
 * a 401, or refused for real (handed to the failed-write log under its account).
 */

/** The objects a sign-out drives, injected so a test runs the real wiring. */
export interface SignOutApp {
  identity: Pick<Identity, 'beginTransition' | 'announceSignedOut'> & {
    cacheAccount: { removeSaved(): Promise<void> };
  };
  writes: { stop(): void };
  uploads: { stop(): void };
  auth: { signOut(): Promise<unknown> };
  location: { assign(to: string): void };
}

function theApp(): SignOutApp {
  return {
    identity,
    writes: pendingWrites,
    uploads: uploadQueue,
    auth: authClient,
    location: globalThis.location,
  };
}

export async function signOutAndReset(
  { to = '/' }: { to?: string } = {},
  app: SignOutApp = theApp(),
): Promise<void> {
  app.identity.beginTransition();
  app.writes.stop();
  app.uploads.stop();
  try {
    await app.auth.signOut();
  } finally {
    try {
      await app.identity.cacheAccount.removeSaved();
    } catch (err) {
      // The purge reminder is left behind: an offline start draws nothing of this account,
      // and the next online start purges it. Reloading is safe either way.
      console.warn('[servwell] the saved cache could not be removed at sign-out', err);
    }
    app.identity.announceSignedOut();
    app.location.assign(to);
  }
}

// ── What is still on the device, for the warning ─────────────────────────────

/** What this account has on the device, counted fresh from each store's disk. */
export type UnsentCounts = Readonly<{
  /** Queued writes not yet sent. */
  changes: number;
  /** Photos not yet sent. */
  photos: number;
  /** Refused writes and refused photos: waiting on a person, not a network. */
  needsYou: number;
}>;

type Counter = {
  countFor(account: AccountRef): Promise<{ waiting: number; refused: number }>;
};

export interface UnsentSources {
  writes: Counter;
  photos: Counter;
  failed: Counter;
}

function theSources(): UnsentSources {
  return { writes: pendingWrites, photos: uploadQueue, failed: failedWriteLog };
}

const NONE = { waiting: 0, refused: 0 };

/**
 * This account's unsent work, from the DISKS (so another tab's counts too), never from
 * memory. Best effort: a store that will not answer counts nothing, and a missed warning
 * loses nothing, because signing out keeps everything.
 */
export async function unsentCountsFor(
  account: AccountRef | null,
  sources: UnsentSources = theSources(),
): Promise<UnsentCounts> {
  if (!account) return { changes: 0, photos: 0, needsYou: 0 };
  const [writes, photos, failed] = await Promise.all(
    [sources.writes, sources.photos, sources.failed].map((s) =>
      s.countFor(account).catch(() => NONE),
    ),
  );
  return {
    changes: writes!.waiting,
    photos: photos!.waiting,
    needsYou: failed!.refused + photos!.refused,
  };
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export type SignOutWarning = Readonly<{
  title: string;
  /** "1 thing needs you", when the title is about unsent work and something was refused. */
  needsYou: string | null;
  body: string;
}>;

/** The warning's words for these counts, or null when there is nothing to warn about. */
export function signOutWarning(c: UnsentCounts): SignOutWarning | null {
  const unsent = c.changes + c.photos;
  const needsYou =
    c.needsYou > 0 ? `${count(c.needsYou, 'thing needs', 'things need')} you` : null;
  if (unsent === 0) {
    if (!needsYou) return null;
    return {
      title: needsYou,
      needsYou: null,
      body:
        c.needsYou === 1
          ? 'It’s saved on this device for the next time you sign in here.'
          : 'They’re saved on this device for the next time you sign in here.',
    };
  }
  const what = [
    c.changes > 0 ? count(c.changes, 'change', 'changes') : null,
    c.photos > 0 ? count(c.photos, 'photo', 'photos') : null,
  ]
    .filter(Boolean)
    .join(' and ');
  const one = unsent === 1;
  return {
    title: `${what} ${one ? 'hasn’t' : 'haven’t'} been sent yet`,
    needsYou,
    body: one
      ? 'It’s saved on this device and will send the next time you sign in here.'
      : 'They’re saved on this device and will send the next time you sign in here.',
  };
}

export type SignOutDecision =
  | { kind: 'offline' }
  | { kind: 'warn'; warning: SignOutWarning }
  | { kind: 'go' };

/** What pressing Sign out does now: explain (offline), warn, or sign out at once. */
export async function decideSignOut(
  account: AccountRef | null,
  online: boolean,
  sources: UnsentSources = theSources(),
): Promise<SignOutDecision> {
  if (!online) return { kind: 'offline' };
  const warning = signOutWarning(await unsentCountsFor(account, sources));
  return warning ? { kind: 'warn', warning } : { kind: 'go' };
}

// ── The guard's state ────────────────────────────────────────────────────────

/** What the guarded control shows: nothing, the offline explanation, or the warning. */
export type SignOutSheetState = Readonly<{
  open: boolean;
  /** Kept while the sheet closes, so its words do not vanish mid-animation. */
  shown: Exclude<SignOutDecision, { kind: 'go' }> | null;
}>;

export type SignOutGuardEvent =
  | { type: 'decided'; decision: SignOutDecision }
  | { type: 'stay' }
  | { type: 'sign-out-anyway' };

export const CLOSED: SignOutSheetState = { open: false, shown: null };

/**
 * One step of the guard: the next sheet, and whether to sign out now. Staying (the default,
 * Esc, the overlay) only closes the sheet; "Sign out anyway" signs out, and only from the
 * warning (offline, signing out cannot happen at all).
 */
export function signOutGuardStep(
  state: SignOutSheetState,
  event: SignOutGuardEvent,
): { state: SignOutSheetState; signOut: boolean } {
  switch (event.type) {
    case 'decided':
      return event.decision.kind === 'go'
        ? { state, signOut: true }
        : { state: { open: true, shown: event.decision }, signOut: false };
    case 'stay':
      return { state: { ...state, open: false }, signOut: false };
    case 'sign-out-anyway':
      return {
        state: { ...state, open: false },
        signOut: state.open && state.shown?.kind === 'warn',
      };
  }
}
